"use client"
import {connect} from "react-redux";
import { useState, useRef, useEffect } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { Send, X, User, Loader2, Workflow, BarChart4 } from "lucide-react"
import { Button } from "../../../components/ui/button"
import { Avatar } from "../../../components/ui/avatar"
import { ScrollArea } from "../../../components/ui/scroll-area"
import { analyzeFlowStats } from "../../../../ApiCalls/mantisFlowsAgent"
import { generateWorkflow } from "../../../../ApiCalls/FlowCreator/morpheus"
import { postAssistantMessage } from "../../../../ApiCalls/mantisBusiness";
import { selectAuthState } from "../../../../store/selectors/authSelectors";

function FlowChat({ flowData, isOpen, onToggle, onEditModeChange, handleStatsMode, onOpenStatsChat, reloadFlow, updateMorpheusWorkflow, user, token, mantisBusiness }) {
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content: "Hola, soy Mantis tu asistente de análisis de datos. ¿En qué puedo ayudarte con este flujo de datos?",
      timestamp: new Date()
    }
  ])
  const [input, setInput] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [isHovered, setIsHovered] = useState(false)
  const [isEditMode, setIsEditMode] = useState(false)
  const [isStatsMode, setIsStatsMode] = useState(false)
  const messagesEndRef = useRef(null)
  const inputRef = useRef(null)

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
  }, [messages])

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        inputRef.current?.focus()
      }, 300)
    }
  }, [isOpen])

  // Función para manejar el modo de estadísticas
  const handleEnterStatsMode = () => {
    // Cerrar este chat
    if (onToggle) onToggle()
    
    // Abrir el chat de estadísticas
    if (onOpenStatsChat) {
      setTimeout(() => {
        onOpenStatsChat()
      }, 300) // Pequeño retraso para permitir que el chat actual se cierre primero
    }
  }

  const handleExitStatsMode = () => {
    setIsStatsMode(false)
    if (handleStatsMode) handleStatsMode(false)
    
    setMessages([
      {
        role: "assistant",
        content: "He salido del modo de estadísticas. ¿En qué más puedo ayudarte con este flujo de datos?",
        timestamp: new Date()
      }
    ])
  }

  const handleSendMessage = async () => {
    if (!input.trim()) return

    const userMessage = {
      role: "user",
      content: input,
      timestamp: new Date()
    }
    
    setMessages(prev => [...prev, userMessage])
    setInput("")
    setIsLoading(true)

    try {
      let responseContent = ""

      if (isStatsMode) {
        // Llamada al agente de estadísticas
        const statsResponse = await analyzeFlowStats(
          input, // prompt del usuario
          flowData.currentStats || [], // estadísticas actuales
          flowData.columnsExample || {}, // ejemplo de columnas
          flowData.idClient || mantisBusiness._id, // ID del cliente
          flowData.idFlow 
        )

        if (statsResponse.success) {
          responseContent = statsResponse.data.response || "He analizado las estadísticas según tu solicitud."
        } else {
          throw new Error(statsResponse.error || "Error al analizar las estadísticas")
        }
      } else if (isEditMode) {
       const res = await generateWorkflow(flowData.idFlow, input, [],  mantisBusiness._id);
       responseContent = res.response?.your_message || "No pude realizar la acción solicitada";
       reloadFlow(true);
       if(res.response){
         updateMorpheusWorkflow(res.response);
       }
      } else {
        // Respuestas para Sapiens
        const userQuery = input.toLowerCase()
        const response = await postAssistantMessage(
          user.cellPhone,
          userQuery,
          token,
          user._id,
          mantisBusiness._id,
          "deep",
          flowData.idFlow
        );
        
        if (response.success) {
          responseContent = response.messages[1].message;
        }else{
          responseContent = "No pude realizar la acción solicitada";
        }
      }
      
      const aiMessage = {
        role: "assistant",
        content: responseContent,
        timestamp: new Date()
      }
      
      setMessages(prev => [...prev, aiMessage])
    } catch (error) {
      console.error("Error al procesar la consulta:", error)
      
      const errorMessage = {
        role: "assistant",
        content: "Lo siento, hubo un error al procesar tu consulta. Por favor, intenta de nuevo más tarde.",
        timestamp: new Date(),
        isError: true
      }
      
      setMessages(prev => [...prev, errorMessage])
    } finally {
      setIsLoading(false)
    }
  }

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault()
      if (input.trim()) {
        handleSendMessage()
      }
    }
  }

  const formatTime = (date) => {
    if (!date) return ""
    return new Date(date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  }

  const handleModifyFlow = () => {
    setIsEditMode(true)
    
    // Notificar al componente padre
    if (onEditModeChange) {
      onEditModeChange(true)
    }
    
    // Cambiar el mensaje del chat
    setMessages([
      {
        role: "assistant",
        content: "Ahora estás en modo de edición de flujo. Puedes pedirme que modifique el flujo según tus instrucciones. ¿Qué cambios te gustaría hacer?",
        timestamp: new Date()
      }
    ])
  }

  const handleExitEditMode = () => {
    setIsEditMode(false)
    
    // Notificar al componente padre
    if (onEditModeChange) {
      onEditModeChange(false)
    }
    
    // Cambiar el mensaje del chat
    setMessages([
      {
        role: "assistant",
        content: "He salido del modo de edición. ¿En qué más puedo ayudarte con este flujo de datos?",
        timestamp: new Date()
      }
    ])
  }

  return (
    <div className="fixed right-0 bottom-0 z-[1000] flex items-end">
      {/* Chat panel */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ x: 400, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: 400, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="h-[calc(100vh-100px)] w-[400px] bg-white dark:bg-slate-800 shadow-xl border-l border-slate-200 dark:border-slate-700 flex flex-col rounded-l-2xl overflow-hidden mb-20 mr-5"
          >
            {/* Header */}
            <div className={`p-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between ${
              isEditMode 
                ? "bg-gradient-to-r from-blue-50 to-white dark:from-blue-900/30 dark:to-slate-800" 
                : isStatsMode
                  ? "bg-gradient-to-r from-purple-50 to-white dark:from-purple-900/30 dark:to-slate-800"
                  : "bg-gradient-to-r from-orange-50 to-white dark:from-slate-700 dark:to-slate-800"
            }`}>
              <div className="flex items-center gap-3">
                <Avatar className={`h-10 w-10 shadow-md flex items-center justify-center ${
                  isEditMode 
                    ? "bg-gradient-to-br from-blue-500 to-blue-600" 
                    : isStatsMode
                      ? "bg-gradient-to-br from-purple-500 to-purple-600"
                      : "bg-gradient-to-br from-orange-500 to-orange-600"
                }`}>
                  <img 
                    src="/mantisWhiteIsotype.png" 
                    alt="Mantis Logo" 
                    className="h-6 w-6 object-contain"
                  />
                </Avatar>
                <div>
                  <h3 className="font-medium text-slate-800 dark:text-slate-200">
                    {isEditMode ? "Asistente de Edición" : isStatsMode ? "Asistente de Estadísticas" : "Asistente Mantis"}
                  </h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    {isEditMode 
                      ? `Modificando ${flowData?.name || 'flujo actual'}`
                      : isStatsMode
                        ? `Analizando estadísticas de ${flowData?.name || 'flujo actual'}`
                        : `Analizando ${flowData?.name || 'flujo actual'}`}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {(isEditMode || isStatsMode) && (
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    onClick={isEditMode ? handleExitEditMode : handleExitStatsMode} 
                    className={`rounded-full ${
                      isEditMode 
                        ? "hover:bg-blue-100 dark:hover:bg-slate-600" 
                        : "hover:bg-purple-100 dark:hover:bg-slate-600"
                    } transition-all duration-200`}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
                <Button 
                  variant="ghost" 
                  size="icon" 
                  onClick={onToggle} 
                  className={`rounded-full ${
                    isEditMode 
                      ? "hover:bg-blue-100 dark:hover:bg-slate-600" 
                      : isStatsMode
                        ? "hover:bg-purple-100 dark:hover:bg-slate-600"
                        : "hover:bg-orange-100 dark:hover:bg-slate-600"
                  } transition-all duration-200`}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
            
            {/* Messages */}
            <ScrollArea className="flex-1">
              <div className="p-4 space-y-4">
                {messages.map((message, index) => (
                  <div key={index} className={`flex ${message.role === "user" ? "justify-end" : "justify-start"}`}>
                    <div className={`flex gap-3 max-w-[85%] ${message.role === "user" ? "flex-row-reverse" : ""}`}>
                      <Avatar className={`h-8 w-8 flex items-center justify-center shadow-sm ${
                        message.role === "user" 
                          ? isEditMode 
                            ? "bg-gradient-to-br from-blue-500 to-blue-600" 
                            : isStatsMode
                              ? "bg-gradient-to-br from-purple-500 to-purple-600"
                              : "bg-gradient-to-br from-orange-500 to-orange-600" 
                          : isEditMode 
                            ? "bg-gradient-to-br from-blue-500 to-blue-600" 
                            : isStatsMode
                              ? "bg-gradient-to-br from-purple-500 to-purple-600"
                              : "bg-gradient-to-br from-orange-500 to-orange-600"
                      }`}>
                        {message.role === "user" ? (
                          <User className="h-4 w-4 text-white" />
                        ) : (
                          <img 
                            src="/mantisWhiteIsotype.png" 
                            alt="Mantis Logo" 
                            className="h-5 w-5 object-contain"
                          />
                        )}
                      </Avatar>
                      <div>
                        <div
                          className={`rounded-2xl p-3 text-sm ${
                            message.role === "user"
                              ? isEditMode 
                                ? "bg-gradient-to-r from-blue-500 to-blue-400 text-white shadow-sm" 
                                : isStatsMode
                                  ? "bg-gradient-to-r from-purple-500 to-purple-400 text-white shadow-sm"
                                  : "bg-gradient-to-r from-orange-500 to-orange-400 text-white shadow-sm"
                              : message.isError
                              ? "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300 shadow-sm"
                              : "bg-slate-100 dark:bg-slate-700 text-slate-800 dark:text-slate-200 shadow-sm"
                          }`}
                        >
                          {message.content}
                        </div>
                        <div className="text-xs text-slate-500 mt-1 px-2">
                          {formatTime(message.timestamp)}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                {isLoading && (
                  <div className="flex justify-start">
                    <div className="flex gap-3 max-w-[85%]">
                      <Avatar className={`h-8 w-8 shadow-sm flex items-center justify-center ${
                        isEditMode 
                          ? "bg-gradient-to-br from-blue-500 to-blue-600" 
                          : isStatsMode
                            ? "bg-gradient-to-br from-purple-500 to-purple-600"
                            : "bg-gradient-to-br from-orange-500 to-orange-600"
                      }`}>
                        <img 
                          src="/mantisWhiteIsotype.png" 
                          alt="Mantis Logo" 
                          className="h-5 w-5 object-contain"
                        />
                      </Avatar>
                      <div>
                        <div className="rounded-2xl p-3 text-sm bg-slate-100 dark:bg-slate-700 text-slate-800 dark:text-slate-200 shadow-sm">
                          <Loader2 className="h-4 w-4 animate-spin" />
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
            </ScrollArea>
            
            {/* Input */}
            <div className="p-4 border-t border-slate-200 dark:border-slate-700 bg-transparent">
              <div className="relative">
                <div className={`flex flex-col w-full relative rounded overflow-hidden border border-slate-200 dark:border-slate-700 ${
                  isEditMode 
                    ? "focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500" 
                    : isStatsMode
                      ? "focus-within:border-purple-500 focus-within:ring-1 focus-within:ring-purple-500"
                      : "focus-within:border-orange-500 focus-within:ring-1 focus-within:ring-orange-500"
                } shadow-sm`}>
                  <div className="relative">
                    <textarea
                      ref={inputRef}
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      onKeyDown={handleKeyDown}
                      placeholder={
                        isEditMode 
                          ? "Escribe tu instrucción de modificación..." 
                          : isStatsMode
                            ? "Escribe tu consulta sobre estadísticas..."
                            : "Escribe tu consulta..."
                      }
                      className="w-full py-3 px-4 bg-transparent border-none focus:outline-none text-slate-800 dark:text-slate-200 resize-none overflow-hidden"
                      style={{ 
                        height: input ? 'auto' : '40px',
                        minHeight: '40px',
                        maxHeight: '150px',
                        overflowY: input.length > 80 ? 'auto' : 'hidden'
                      }}
                      rows={1}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between p-2 bg-transparent">
                    <div className="flex gap-2">
                      {!isEditMode && !isStatsMode ? (
                        <>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={handleModifyFlow}
                            className="text-xs flex items-center gap-1 text-orange-600 hover:bg-orange-500 hover:text-white dark:text-orange-400 dark:hover:bg-orange-600 dark:hover:text-white rounded transition-all duration-200 h-7 px-2 border-none shadow-none"
                          >
                            <Workflow className="h-3 w-3" />
                            <span className="text-xs">Modificar</span>
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={handleEnterStatsMode}
                            className="text-xs flex items-center gap-1 text-orange-600 hover:bg-orange-500 hover:text-white dark:text-orange-400 dark:hover:bg-orange-600 dark:hover:text-white rounded transition-all duration-200 h-7 px-2 border-none shadow-none"
                          >
                            <BarChart4 className="h-3 w-3" />
                            <span className="text-xs">Estadísticas</span>
                          </Button>
                        </>
                      ) : isEditMode ? (
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={handleExitEditMode}
                          className="text-xs flex items-center gap-1 text-blue-600 hover:bg-blue-500 hover:text-white dark:text-blue-400 dark:hover:bg-blue-600 dark:hover:text-white rounded transition-all duration-200 h-7 px-2 border-none shadow-none"
                        >
                          <X className="h-3 w-3" />
                          <span className="text-xs">Salir</span>
                        </Button>
                      ) : (
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={handleExitStatsMode}
                          className="text-xs flex items-center gap-1 text-purple-600 hover:bg-purple-500 hover:text-white dark:text-purple-400 dark:hover:bg-purple-600 dark:hover:text-white rounded transition-all duration-200 h-7 px-2 border-none shadow-none"
                        >
                          <X className="h-3 w-3" />
                          <span className="text-xs">Salir</span>
                        </Button>
                      )}
                    </div>
                    
                    <Button 
                      onClick={handleSendMessage} 
                      disabled={isLoading || !input.trim()}
                      className={`h-8 w-8 rounded-full flex items-center justify-center ${
                        isEditMode 
                          ? "bg-blue-500 hover:bg-blue-600" 
                          : isStatsMode
                            ? "bg-purple-500 hover:bg-purple-600"
                            : "bg-orange-500 hover:bg-orange-600"
                      }`}
                    >
                      <Send className="h-4 w-4 text-white" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Botón circular flotante para abrir el chat */}
      <AnimatePresence>
        {!isOpen && (
          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed right-6 bottom-6 flex items-center"
          >
            <AnimatePresence>
              {isHovered && (
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  className="mr-2 bg-white dark:bg-slate-700 px-3 py-1 rounded shadow-md text-sm font-medium text-slate-800 dark:text-slate-200"
                >
                  Chat Mantis
                </motion.div>
              )}
            </AnimatePresence>
            <Button
              variant="default"
              onClick={onToggle}
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
              className="h-14 w-14 rounded-full bg-gradient-to-r from-orange-500 to-orange-400 hover:from-orange-600 hover:to-orange-500 shadow-lg flex items-center justify-center transition-all duration-200 hover:scale-110 p-0"
            >
              <img 
                src="/mantisChat.png" 
                alt="Mantis Logo" 
                className="h-10 w-10 object-contain p-0.5"
              />
            </Button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default connect(selectAuthState)(FlowChat);